import { ColDef } from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { ManagedTickerData } from '../../../apis/ticker-management/ticker-management-api.def.ts'
import { CheckOutlined, EditOutlined } from '@ant-design/icons'
import { ICellRendererParams } from 'ag-grid-enterprise'
import React, { useMemo, useRef } from 'react'
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react'
import { TICKER_MANAGEMENT_COLUMN_STATE_KEY } from '../../../utils/constants.ts'
import { IdeyaButton } from '../../../common/antd/ideya-button.tsx'
import { selectManagementInput } from '../../../redux/slices/management-input.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'
import { useTranslation } from 'react-i18next'

const BooleanCellRenderer: React.FC<ICellRendererParams> = (props) => {
  return <span>{props.value ? <CheckOutlined /> : ''}</span>
}

interface TickerManagementTableProps {
  isLoading: boolean
  isFetching: boolean
  tickerManagementList: ManagedTickerData[]
  onEditTicker: (id: string) => void
  height: number
}

export const TickerManagementTable: React.FC<TickerManagementTableProps> = ({
  isLoading,
  isFetching,
  tickerManagementList,
  onEditTicker,
  height,
}) => {
  const gridRef = useRef<AgGridReact>(null)
  const { t } = useTranslation()
  const managementInput = useAppSelector(selectManagementInput)

  const columnDefs: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: t('ticker_management.table.columns.ticker_name'),
        field: 'name',
      },
      {
        headerName: t('ticker_management.table.columns.bbg_yellow_key'),
        field: 'bbgYellowKey',
      },
      {
        headerName: t('ticker_management.table.columns.figi'),
        field: 'figi',
      },
      {
        headerName: t('ticker_management.table.columns.ric'),
        field: 'ric',
      },
      {
        headerName: t('ticker_management.table.columns.category'),
        field: 'region',
      },
      {
        headerName: t('ticker_management.table.columns.can_long'),
        field: 'canLong',
        cellRenderer: BooleanCellRenderer,
      },
      {
        headerName: t('ticker_management.table.columns.can_short'),
        field: 'canShort',
        cellRenderer: BooleanCellRenderer,
      },
      {
        headerName: t('ticker_management.table.columns.state'),
        field: 'status',
      },
      {
        headerName: t('ticker_management.table.columns.start_date'),
        field: 'startDate',
      },
      {
        headerName: t('ticker_management.table.columns.end_date'),
        field: 'endDate',
      },
      {
        headerName: t('ticker_management.table.columns.is_adr'),
        field: 'isAdr',
        cellRenderer: BooleanCellRenderer,
      },
    ]

    const savedState = localStorage.getItem(TICKER_MANAGEMENT_COLUMN_STATE_KEY)
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        const colOrder = state.map((col: { colId: string }) => col.colId)
        const ordered = colOrder
          .map((id: string) => baseColumns.find((col) => col.field === id))
          .filter(Boolean)
        const missing = baseColumns.filter(
          (col) => !colOrder.includes(col.field)
        )
        return [...ordered, ...missing]
      } catch {
        return baseColumns
      }
    }

    if (!managementInput.expiredTicker) {
      return [
        {
          headerName: '',
          field: 'id',
          width: 50,
          cellRenderer: (props: CustomCellRendererProps) => {
            return (
              <IdeyaButton
                icon={
                  <EditOutlined onClick={() => onEditTicker(props.data.id)} />
                }
              />
            )
          },
        },
        ...baseColumns,
      ]
    }

    return baseColumns
  }, [managementInput.expiredTicker, onEditTicker, t])

  const handleGridReady = () => {
    if (!gridRef.current) return
    const savedState = localStorage.getItem(TICKER_MANAGEMENT_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)
      gridRef.current.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }

  const handleColumnChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      TICKER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const handleSortChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      TICKER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  return (
    <IdeyaAgGridTable
      ref={gridRef}
      columnDefs={columnDefs}
      rowData={tickerManagementList}
      loading={isLoading || isFetching}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onSortChanged={handleSortChanged}
      height={height}
    />
  )
}
